/**
 * GUPM-302 — detecta que saiu deploy novo enquanto esta aba estava aberta.
 *
 * Gatilhos, e por quê — cada um existe porque os outros não alcançam o caso dele:
 *   - **no mount** — o hook só existe dentro do `AppShell`, que é rota protegida. Quem
 *     logou agora pode estar entrando numa aba que ficou parada no `/login` durante um
 *     deploy: os outros gatilhos não alcançam esse caso.
 *   - **na troca de tela** — o `AppShell` é rota-layout: navegar substitui só o
 *     `<Outlet/>` e o shell NÃO remonta. Sem este gatilho, quem está de fato *usando* o
 *     app era o pior servido — uma verificação no login e depois nada por até 12 min —
 *     enquanto quem apenas volta à aba era avisado na hora.
 *   - `visibilitychange` — o operador de plantão deixa a tela aberta por horas e volta
 *     nela; verificar quando a aba fica visível entrega o aviso no momento em que ele
 *     está olhando.
 *   - intervalo de 12 min, **só com a aba visível** — é o único caminho para a aba
 *     visível e parada (dashboard num segundo monitor). Lento de propósito: não é uma
 *     corrida, é um aviso.
 *
 * O hook NUNCA recarrega — só informa. Quem escolhe o momento é o usuário
 * (`AvisoNovaVersao`). Recarregar sozinho tiraria a tela de vista de um operador no
 * meio de uma TARP.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { extrairScriptDeModulo, temVersaoNova } from '@/domain/versao/deploy';

/**
 * Rede de segurança para a aba que fica VISÍVEL e parada — o único caso que nenhum
 * outro gatilho alcança.
 */
const INTERVALO_MS = 12 * 60 * 1000;

/**
 * Piso entre duas verificações, qualquer que seja o gatilho. Sem ele, navegar pelo menu
 * lateral viraria uma requisição por clique; com ele, rajada de navegação — e alt-tab
 * repetido — colapsa em uma. É a janela que torna seguro ligar a verificação a um
 * gatilho tão frequente quanto a troca de tela.
 */
const JANELA_MINIMA_MS = 60 * 1000;

/**
 * Identidade do chunk que ESTA aba está rodando: o `src` do script de módulo do
 * documento atual. É o próprio `index.html` que o navegador carregou.
 */
function identidadeLocal(): string | null {
  const el = document.querySelector<HTMLScriptElement>('script[type="module"][src]');
  return el?.getAttribute('src') ?? null;
}

export function useNovaVersao(): { temNova: boolean; recarregar: () => void } {
  const [temNova, setTemNova] = useState(false);
  // Uma vez detectado, não volta atrás: a aba segue obsoleta até recarregar.
  const detectado = useRef(false);
  // Começa em 0 para que a primeira verificação (o mount) nunca caia na janela.
  const ultimaVerificacaoMs = useRef(0);
  // Uma linha por sessão, não uma a cada 12 min — ver `avisarCanalMudo`.
  const jaAvisouCanalMudo = useRef(false);
  const { pathname } = useLocation();

  /**
   * O canal de aviso parou de funcionar. Silêncio para o USUÁRIO continua certo — aviso
   * falso gasta a confiança que o aviso certo vai precisar. Silêncio para quem MANTÉM o
   * app, não: se a verificação morrer de vez (proxy que bloqueia, CSP nova, borda
   * devolvendo erro), o único sintoma é a AUSÊNCIA de um aviso que ninguém estava
   * esperando ver — do lado de fora, indistinguível de "está tudo em dia".
   *
   * Uma vez por sessão é o ponto entre os dois: dá o rastro sem virar o ruído a cada 12
   * min que o desenho original recusou, com razão. `warn` e não `error`: nada quebrou
   * para quem está operando, só uma verificação de fundo ficou cega.
   */
  const avisarCanalMudo = useCallback((motivo: string) => {
    if (jaAvisouCanalMudo.current) return;
    jaAvisouCanalMudo.current = true;
    console.warn(`[versao] verificacao de nova versao indisponivel nesta sessao: ${motivo}`);
  }, []);

  const verificar = useCallback(async () => {
    if (detectado.current) return;
    // A janela mora aqui, e não em cada gatilho, para valer para TODOS eles — inclusive
    // combinações (montar e navegar no mesmo instante, alt-tab em rajada). O carimbo é
    // gravado ANTES do `await`, senão duas chamadas simultâneas passariam as duas.
    const agora = Date.now();
    if (agora - ultimaVerificacaoMs.current < JANELA_MINIMA_MS) return;
    ultimaVerificacaoMs.current = agora;
    try {
      // `no-store` é obrigatório: sem ele o navegador poderia servir cópia cacheada do
      // index.html e o aviso nunca apareceria.
      const resp = await fetch('/', { cache: 'no-store' });
      if (!resp.ok) {
        // O caminho traiçoeiro: `fetch` RESOLVEU, então não há exceção nenhuma para o
        // `catch` pegar. Sem este aviso, um 503 da borda ou uma regra de redirect errada
        // deixaria a verificação rodando para sempre sem nunca poder concluir nada.
        avisarCanalMudo(`resposta HTTP ${resp.status}`);
        return;
      }
      const remoto = extrairScriptDeModulo(await resp.text());
      const local = identidadeLocal();
      if (!local || !remoto) {
        // O terceiro caminho mudo, e o mais provável de acontecer: 200 com HTML de
        // verdade — sem exceção e sem `!resp.ok` — mas sem `<script type="module">` para
        // ler. Interstitial de WAF, página de manutenção, "checking your browser", ou o
        // Vite mudando a forma da tag.
        //
        // `temVersaoNova` já devolve `false` para identidade nula e isso continua certo:
        // leitura falha não pode virar aviso falso. O que faltava era alguém FICAR
        // SABENDO que a leitura falhou, em vez de a verificação seguir rodando a cada 12
        // min sem nunca poder concluir nada.
        avisarCanalMudo(
          `identidade ilegivel (local=${local ?? 'nula'}, remoto=${remoto ?? 'nula'})`,
        );
        return;
      }
      if (temVersaoNova(local, remoto)) {
        detectado.current = true;
        setTemNova(true);
      }
    } catch (erro) {
      // Falha de rede/parse não vira aviso na TELA: aviso falso é pior que aviso ausente
      // neste app. Mas vira uma linha no console, uma vez por sessão.
      avisarCanalMudo(erro instanceof Error ? erro.message : String(erro));
    }
  }, [avisarCanalMudo]);

  // MOUNT + TROCA DE TELA são o mesmo efeito de propósito: o `pathname` inicial já é uma
  // execução, então o mount sai de graça e não há duas requisições no carregamento.
  //
  // O mount fecha o buraco dos outros gatilhos: este hook vive no `AppShell`, dentro de
  // `<ProtectedRoute>`, então não existe enquanto a pessoa está no `/login`. Se a aba
  // ficou parada ali durante um deploy, o `visibilitychange` que a traria de volta
  // disparou sem ninguém escutando. Vale igual para logout→login na mesma aba, em que o
  // estado do aviso se perde na remontagem.
  //
  // A troca de tela cobre quem está usando o app: sem ela, o usuário ativo só era
  // avisado no intervalo, apesar de estar clicando na tela o tempo todo. A janela mínima
  // é o que impede isso de virar uma requisição por clique de menu.
  useEffect(() => {
    void verificar();
  }, [pathname, verificar]);

  useEffect(() => {
    // Aba oculta não recebe verificação: não há o que avisar para quem não está olhando,
    // e o `visibilitychange` já verifica no instante em que ela volta. Sem esta guarda o
    // intervalo seguia batendo na rede numa aba esquecida em segundo plano — 5
    // requisições por hora, indefinidamente, todas descartadas. O app é mobile-first
    // para operador em campo sem 4G estável (CLAUDE.md, regra 10).
    const verificarSeVisivel = () => {
      if (document.visibilityState !== 'hidden') void verificar();
    };
    document.addEventListener('visibilitychange', verificarSeVisivel);
    const timer = setInterval(verificarSeVisivel, INTERVALO_MS);
    return () => {
      document.removeEventListener('visibilitychange', verificarSeVisivel);
      clearInterval(timer);
    };
  }, [verificar]);

  const recarregar = useCallback(() => window.location.reload(), []);

  return { temNova, recarregar };
}
